import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../auth-lib';
import { canRecord, startRecording, filenameFor, explainVoiceReason } from '../lib/voice';
import './VoiceButton.css';

// The mic. One component, used by the quick-add dialog and the AI command bar,
// so dictation behaves identically wherever it appears.
//
// ⚠️ HOLD TO RECORD, RELEASE TO SEND — the WhatsApp gesture, because it is the
// one every person in the firm already has in their fingers. Everything below
// exists to make holding survive a touch screen:
//
//   · POINTER CAPTURE, so the release is heard even if the finger slides off
//     the button, onto the page, or outside the dialog. Without it a press that
//     drifts a few pixels never ends and the mic stays open.
//   · `touch-action: none`, so holding does not scroll the page under the
//     finger and hand the browser a gesture it will cancel the press over.
//   · SLIDE LEFT TO CANCEL, so a hold begun by mistake has a way out that is
//     not "let go and hope" — again, the gesture people already know.
//   · A MINIMUM HOLD, so a stray tap is not a half-second of silence sent to a
//     transcriber that will answer it with "Thank you."
//
// ⚠️ IT HANDS BACK TEXT. It never creates anything and never decides what the
// words meant — `onTranscript` puts them in the box and a person reads them
// before anything happens.

// Below this a press is a tap, not a sentence.
const MIN_HOLD_MS = 400;
// How far left the finger travels before release means cancel. Roughly a
// thumb's width, matching the distance WhatsApp uses.
const CANCEL_SLIDE_PX = 60;

const MicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M8 1.75a2 2 0 0 1 2 2v4a2 2 0 1 1-4 0v-4a2 2 0 0 1 2-2Z" fill="currentColor" />
    <path d="M3.75 7a4.25 4.25 0 0 0 8.5 0M8 11.25v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const StopIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="8" height="8" rx="1.5" fill="currentColor" />
  </svg>
);

const BinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const clock = (ms) => {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

function VoiceButton({ onTranscript, onError, label = 'Hold to speak', autoStart = false, disabled = false }) {
  const [state, setState] = useState('idle'); // idle | recording | sending
  const [cancelArmed, setCancelArmed] = useState(false);
  // ⚠️ LOCKED IS THE SHORTCUT'S MODE, NOT A SECOND BUTTON. The Android
  // home-screen shortcut opens this already recording, and there is no finger
  // held down to release — so that session is stopped by a tap instead, exactly
  // as WhatsApp's slide-up lock behaves.
  const [locked, setLocked] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [note, setNote] = useState('');

  const sessionRef = useRef(null);
  const aliveRef = useRef(true);
  const startedAtRef = useRef(0);
  const originXRef = useRef(0);
  const cancelRef = useRef(false);
  const keyHeldRef = useRef(false);
  // ⚠️ THE REF DECIDES; THE STATE ONLY DRAWS. A finger can slide past the
  // threshold and lift inside the same frame, and React has not re-rendered in
  // between — so `cancelArmed` read from state in the pointerup handler is
  // still the previous value, and a recording the user threw away gets sent.
  // The ref is written synchronously on every move, so the release always reads
  // what the finger actually did.
  const cancelArmedRef = useRef(false);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      // ⚠️ THE MIC MUST NOT SURVIVE THE COMPONENT. Closing the dialog
      // mid-sentence unmounts this while a recorder is live; without the
      // cleanup the track stays open and the phone goes on saying the app is
      // listening.
      aliveRef.current = false;
      sessionRef.current?.cancel();
      sessionRef.current = null;
    };
  }, []);

  // The counter. Runs only while recording, so an idle button costs nothing.
  useEffect(() => {
    if (state !== 'recording') return undefined;
    const id = setInterval(() => setElapsed(Date.now() - startedAtRef.current), 200);
    return () => clearInterval(id);
  }, [state]);

  const fail = useCallback((reason) => {
    const message = explainVoiceReason(reason);
    setNote(message);
    onError?.(message, reason);
  }, [onError]);

  const send = useCallback(async (blob, mimeType) => {
    if (!blob || blob.size < 1200) {
      setState('idle');
      fail('no_speech');
      return;
    }

    setState('sending');
    try {
      const form = new FormData();
      form.append('audio', blob, filenameFor(mimeType));
      const { data } = await apiClient.post('/api/ai/transcribe', form);

      if (!aliveRef.current) return;
      if (data?.text) {
        setNote('');
        onTranscript?.(data.text);
      } else {
        fail(data?.reason);
      }
    } catch (err) {
      if (!aliveRef.current) return;
      fail(err.response?.data?.reason || 'unavailable');
    } finally {
      if (aliveRef.current) setState('idle');
    }
  }, [fail, onTranscript]);

  const begin = useCallback(async ({ lock = false } = {}) => {
    if (!canRecord()) { fail('unsupported'); return; }
    if (sessionRef.current) return;

    setNote('');
    setElapsed(0);
    cancelRef.current = false;
    cancelArmedRef.current = false;
    setCancelArmed(false);
    startedAtRef.current = Date.now();

    try {
      const session = await startRecording({
        onAutoStop: () => setNote('Stopped at a minute.'),
      });
      // Let go before the mic even opened — throw it away rather than leaving a
      // recorder running that nothing is going to stop.
      if (!aliveRef.current || cancelRef.current) { session.cancel(); return; }
      sessionRef.current = session;
      setLocked(lock);
      setState('recording');
    } catch (err) {
      fail(err?.name === 'NotAllowedError' || err?.name === 'SecurityError' ? 'denied' : 'unsupported');
    }
  }, [fail]);

  const finish = useCallback(async ({ cancelled = false } = {}) => {
    cancelRef.current = true;
    const session = sessionRef.current;
    const heldFor = Date.now() - startedAtRef.current;
    sessionRef.current = null;
    cancelArmedRef.current = false;
    setCancelArmed(false);
    setLocked(false);

    if (!session) { setState('idle'); return; }

    if (cancelled) {
      session.cancel();
      setState('idle');
      setNote('Cancelled.');
      return;
    }

    // ⚠️ A TAP IS NOT A SENTENCE. Anything under the minimum is discarded with
    // a nudge rather than uploaded — it would come back as "you" and the server
    // would reject it anyway, a round trip to say nothing.
    if (heldFor < MIN_HOLD_MS) {
      session.cancel();
      setState('idle');
      setNote('Hold the button while you speak.');
      return;
    }

    const blob = await session.stop();
    if (!aliveRef.current) return;
    await send(blob, session.mimeType);
  }, [send]);

  // ── The gesture ───────────────────────────────────────────────────────────

  const onPointerDown = (e) => {
    if (disabled || state === 'sending') return;
    if (locked) { finish(); return; }
    // ⚠️ CAPTURE BEFORE ANYTHING ELSE. From here every move and the release
    // are delivered to this element even when the finger leaves it, which is
    // what makes "slide away to cancel" possible at all.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    originXRef.current = e.clientX;
    begin();
  };

  const onPointerMove = (e) => {
    if (state !== 'recording' || locked) return;
    const armed = originXRef.current - e.clientX > CANCEL_SLIDE_PX;
    cancelArmedRef.current = armed;
    setCancelArmed(armed);
  };

  const onPointerUp = (e) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (locked || state !== 'recording') return;
    finish({ cancelled: cancelArmedRef.current });
  };

  // The browser taking the gesture away — a system dialog, a notification
  // shade pulled down mid-hold. Treated as cancel, never as send: nobody
  // finished saying anything.
  const onPointerCancel = () => {
    if (state === 'recording' && !locked) finish({ cancelled: true });
  };

  // ⚠️ A BUTTON YOU MUST HOLD IS UNUSABLE WITH A KEYBOARD unless this exists.
  // Holding Space or Enter fires repeating keydowns; the ref makes the first
  // one start and the rest no-ops, and keyup ends it. Escape cancels.
  const onKeyDown = (e) => {
    if (disabled || state === 'sending') return;
    if (e.key === 'Escape' && state === 'recording') { finish({ cancelled: true }); return; }
    if (e.key !== ' ' && e.key !== 'Enter') return;
    e.preventDefault();
    if (keyHeldRef.current) return;
    keyHeldRef.current = true;
    if (locked) finish(); else begin();
  };

  const onKeyUp = (e) => {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    keyHeldRef.current = false;
    if (!locked && state === 'recording') finish();
  };

  // The home-screen shortcut opens this already listening. The tap that opened
  // it is the user gesture getUserMedia needs.
  useEffect(() => {
    if (autoStart && canRecord() && !disabled) begin({ lock: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  // Hidden entirely rather than shown broken: a mic on a browser that cannot
  // record is a button whose only function is to apologise.
  if (!canRecord()) return null;

  const recording = state === 'recording';
  const sending = state === 'sending';

  const text = sending ? 'Transcribing…'
    : cancelArmed ? 'Release to cancel'
      : locked ? 'Tap to stop'
        : recording ? clock(elapsed)
          : label;

  return (
    <div className="voice-button-wrap">
      <button
        type="button"
        className={[
          'voice-button',
          recording ? 'is-recording' : '',
          cancelArmed ? 'is-cancelling' : '',
        ].filter(Boolean).join(' ')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        // A long press on a touch screen otherwise raises the copy/paste
        // callout or the image menu over the button being held.
        onContextMenu={(e) => e.preventDefault()}
        disabled={disabled || sending}
        aria-pressed={recording}
        aria-label={recording ? 'Recording — release to send, slide left to cancel' : label}
      >
        {cancelArmed ? <BinIcon /> : recording ? <StopIcon /> : <MicIcon />}
        <span className="voice-button-text">{text}</span>
      </button>

      {recording && !locked && (
        <p className="voice-button-hint" aria-hidden="true">‹ Slide left to cancel</p>
      )}
      {/* role=status, so a screen reader hears the outcome without focus being
          dragged off whatever is being typed into. */}
      {note && <p className="voice-button-note" role="status">{note}</p>}
    </div>
  );
}

export default VoiceButton;
